import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Theme } from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import Divider from "@material-ui/core/Divider";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Toolbar from "@material-ui/core/Toolbar";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import RoomIcon from "@material-ui/icons/Room";
import * as Dati from "../../../Dati";
import * as Glob from "../../../Globale";
import { Trad } from "../../../components";
import { _Autocomplete, _AutocompletePlace, _DateField, _Select } from "../../form";
import { MainAddressIndex } from "../../pages/ClientDetails";

interface IAddressesProps {
    Data: Glob.DatiJSON;
    DetailType: string;
    OnChange: (val: string) => void;
}

const useStyles = makeStyles(({ shape, spacing, palette }: Theme) => ({
    icon: {
        marginRight: spacing(2)
    },
    title: {
        flexGrow: 1
    },
    toolbar: {
        borderRadius: shape.borderRadius,
        background: palette.info.light,
        color: palette.info.contrastText,
        marginTop: spacing(4),
        marginBottom: spacing(2)
    },
    removeBtn: {
        color: palette.error.main,
        background: "none"
    },
    divider: {
        marginTop: spacing(3),
        marginBottom: spacing(3)
    }
}));

const FirmAddressTypes = ["Sede Legale", "Sede Principale", "Sede Secondaria"];
const PersonAddressTypes = ["Domicilio", "Residenza", "Ufficio"];

export const _Addresses: React.FC<IAddressesProps> = ({ Data, DetailType, OnChange }) => {
    const classes = useStyles();

    const addresses: Dati.CDatoIndirizzo[] = Data ? JSON.parse(Data) : [];
    const isMainAddressRequired: boolean = !!DetailType && DetailType != Glob.Tipi_TAP_ATI;
    const isMainAddressSet: boolean = !!addresses.filter(address => address.Tipo.includes(0)).length;

    const OnAddressChange = (name: string, val: string, index: number) => {
        addresses[index][name] = val;
        OnChange(JSON.stringify(addresses));
    };

    const OnAddressTypeChange = (val: number, index: number) => {
        if (addresses[index]["Tipo"].includes(val)) {
            addresses[index]["Tipo"] = addresses[index]["Tipo"].filter(item => item != val);
        } else {
            addresses[index]["Tipo"].push(val);
        }
        OnChange(JSON.stringify(addresses));
    };

    const OnRemoveAddress = (index: number) => {
        addresses.splice(index, 1);
        if (addresses.length > 0 && isMainAddressRequired) {
            addresses[0].Tipo = [MainAddressIndex];
        }
        OnChange(JSON.stringify(addresses));
    };

    const OnAddAddress = () => {
        const addedAddress = new Dati.CDatoIndirizzo();

        if (addresses.length == 0 && isMainAddressRequired) {
            addedAddress.Tipo = [MainAddressIndex];
        }
        addresses.push(addedAddress);
        OnChange(JSON.stringify(addresses));
    };

    const OnPlaceChange = (index: number) => (address: any) => {
        const { administrative_area_level_2 = "", administrative_area_level_3 = "", country = "", locality = "", postal_code = "", route = "", street_number = "" } = address;

        addresses[index] = {
            ...addresses[index],
            Indirizzo: route || street_number ? `${route} ${street_number}` : "",
            Citta: locality || administrative_area_level_3,
            Cap: postal_code,
            Provincia: administrative_area_level_2,
            Nazione: country
        };
        OnChange(JSON.stringify(addresses));
    };

    const getAddressTypes = () => {
        switch (DetailType) {
            case Glob.Tipi_TAP_PersonaGiuridica:
            case Glob.Tipi_TAP_ATI:
                return FirmAddressTypes;
            case Glob.Tipi_TAP_PersonaFisica:
            case Glob.Tipi_TAP_DittaIndividuale:
                return PersonAddressTypes;
            default:
                return [...FirmAddressTypes, ...PersonAddressTypes];
        }
    };

    const addressTypes = getAddressTypes();

    return (
        <>
            <Toolbar variant="dense" className={classes.toolbar}>
                <RoomIcon className={classes.icon} />
                <Typography variant="subtitle1" color="inherit" className={classes.title}>
                    {Trad.Trad("Indirizzi")}
                </Typography>
                <Tooltip title={Trad.Trad("Nuovo indirizzo")}>
                    <IconButton size="small" aria-label="add address" onClick={OnAddAddress}>
                        <AddIcon />
                    </IconButton>
                </Tooltip>
            </Toolbar>
            {addresses.map((address: Dati.CDatoIndirizzo, index: number) => {
                const isAddressDisabled = !(address.Indirizzo || address.Citta || address.Cap || address.Provincia || address.Nazione);

                return (
                    <div key={index}>
                        <Grid container spacing={4} justify="space-between" alignItems="center" key={index}>
                            <Grid item xs={12} md={6}>
                                <_AutocompletePlace OnPlaceChange={OnPlaceChange(index)} />
                            </Grid>
                            <Grid item xs={12} md={11} container spacing={4}>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        disabled={isAddressDisabled}
                                        value={address.Indirizzo}
                                        id="Indirizzo"
                                        label={Trad.Trad("Indirizzo")}
                                        onChange={e => OnAddressChange(e.currentTarget.id, e.currentTarget.value, index)}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        disabled={isAddressDisabled}
                                        value={address.Citta}
                                        id="Citta"
                                        label={Trad.Trad("Citta")}
                                        onChange={e => OnAddressChange(e.currentTarget.id, e.currentTarget.value, index)}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        disabled={isAddressDisabled}
                                        value={address.Cap}
                                        id="Cap"
                                        label={Trad.Trad("CAP")}
                                        onChange={e => OnAddressChange(e.currentTarget.id, e.currentTarget.value, index)}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        disabled={isAddressDisabled}
                                        value={address.Provincia}
                                        id="Provincia"
                                        label={Trad.Trad("Provincia")}
                                        onChange={e => OnAddressChange(e.currentTarget.id, e.currentTarget.value, index)}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        disabled={isAddressDisabled}
                                        value={address.Nazione}
                                        id="Nazione"
                                        label={Trad.Trad("Nazione")}
                                        onChange={e => OnAddressChange(e.currentTarget.id, e.currentTarget.value, index)}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item>
                                    <FormGroup row>
                                        {addressTypes.map((addressType: string, i: number) => {
                                            const isChecked: boolean = address.Tipo.includes(i);

                                            return (
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={isChecked}
                                                            disabled={i == 0 && ((isChecked && isMainAddressRequired) || isMainAddressSet)}
                                                            onChange={e => {
                                                                OnAddressTypeChange(i, index);
                                                            }}
                                                            name={addressType}
                                                        />
                                                    }
                                                    label={Trad.Trad(addressType)}
                                                    key={addressType}
                                                />
                                            );
                                        })}
                                    </FormGroup>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <Tooltip title={Trad.Trad("Elimina")}>
                                    <IconButton
                                        disableRipple
                                        className={classes.removeBtn}
                                        aria-label="remove address"
                                        onClick={() => {
                                            OnRemoveAddress(index);
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                        {index < addresses.length - 1 && <Divider light className={classes.divider} />}
                    </div>
                );
            })}
        </>
    );
};



// WEBPACK FOOTER //
// ./src/inar2/components/ClientDetails/Addresses.tsx